import { EventBus, isWebSocketOpen, sendMessage } from "@/websocket";
import { cardReaderStatus } from "@/utils/cardReaderStatus";
import { parseReadSecLine } from "@/utils/rfidProtocol";

const FIELD_ORDER = ["phone", "cardNo", "balance", "points"];
let writeBusyUntil = 0;

export function memberCardWriteBusy() {
  return Date.now() < writeBusyUntil;
}

function encodeBlock(text) {
  const value = String(text || "");
  if (value.length > 16) return "";
  const bytes = new Array(16).fill(0);
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code > 127) return "";
    bytes[i] = code;
  }
  return bytes.map((item) => item.toString(16).padStart(2, "0")).join("").toUpperCase();
}

function bytesToHex(bytes) {
  return bytes.map((item) => item.toString(16).padStart(2, "0")).join("").toUpperCase();
}

function pickPlan() {
  const sectors = [];
  for (let sector = 2; sector <= 15; sector += 1) sectors.push(sector);
  for (let i = sectors.length - 1; i > 0; i -= 1) {
    const swap = Math.floor(Math.random() * (i + 1));
    const current = sectors[i];
    sectors[i] = sectors[swap];
    sectors[swap] = current;
  }
  return sectors.slice(0, FIELD_ORDER.length).map((sector) => ({
    sector,
    block: Math.floor(Math.random() * 3),
  }));
}

function encodeIndex(plan) {
  const bytes = new Array(16).fill(0);
  bytes[0] = 0x43;
  bytes[1] = 0x50;
  bytes[2] = 0x01;
  plan.forEach((item, index) => {
    bytes[3 + index * 2] = item.sector;
    bytes[4 + index * 2] = item.block;
  });
  return bytesToHex(bytes);
}

function memberFields(user) {
  const phone = String(user.phone || "").replace(/\D/g, "").slice(0, 15);
  const cardNo = String(user.cardno || "").replace(/\s/g, "").slice(0, 16);
  const currency = String(user.currey || user.currency || "CNY").trim().toUpperCase().slice(0, 3);
  const amount = user.amount == null || user.amount === "" ? "0" : String(user.amount).trim();
  const points = user.point == null || user.point === "" ? "0" : String(parseInt(user.point, 10) || 0);
  const cardpass = String(user.cardpass || "").trim();
  const balanceText = amount + "|" + currency;
  if (!/^\d{1,15}$/.test(phone) || !/^[0-9A-Za-z]{1,16}$/.test(cardNo)) return null;
  if (!/^[A-Z]{3}$/.test(currency) || balanceText.length > 16) return null;
  if (!/^\d{1,10}$/.test(points)) return null;
  if (!cardpass || cardpass.length > 16 || /\s/.test(cardpass)) return null;
  return { phone, cardNo, balance: balanceText, points, cardpass };
}

export function writeMemberCard(user) {
  if (!user || !String(user.hex || "").trim()) return "skip";
  if (!isWebSocketOpen() || cardReaderStatus.state !== "connected") return "offline";
  const fields = memberFields(user);
  if (!fields) return "invalid";
  const plan = pickPlan();
  const blocks = [
    encodeBlock(fields.phone),
    encodeBlock(fields.cardNo),
    encodeBlock(fields.balance),
    encodeBlock(fields.points),
  ];
  if (blocks.some((item) => !item)) return "invalid";
  const lines = ["MODE SECTOR", "CRYPTKEY " + fields.cardpass];
  plan.forEach((item, index) => {
    lines.push("WRITESEC " + item.sector + " " + item.block + " " + blocks[index]);
  });
  lines.push("WRITESEC 1 0 " + encodeIndex(plan));
  lines.push("MODE SCAN");
  writeBusyUntil = Date.now() + lines.length * 280 + 800;
  lines.forEach((line, index) => {
    setTimeout(() => {
      sendMessage(JSON.stringify({ type: "command", command: "write", payload: line }));
    }, index * 280);
  });
  return "sent";
}

export function notifyMemberCardWrite(vm, result) {
  if (result === "sent") {
    vm.$message.success(vm.$t("menuitems.clouds.nfcreader.rfid.cardWritten"));
  } else if (result === "offline") {
    vm.$message.warning(vm.$t("menuitems.clouds.nfcreader.rfid.readerRequired"));
  } else if (result === "invalid") {
    vm.$message.warning(vm.$t("menuitems.clouds.nfcreader.rfid.fieldInvalid"));
  }
}

function decodeBlock(hex) {
  let text = "";
  for (let i = 0; i < String(hex || "").length; i += 2) {
    const value = parseInt(String(hex).substr(i, 2), 16);
    if (!value) break;
    text += String.fromCharCode(value);
  }
  return text;
}

export function hexToCardText(hex) {
  return decodeBlock(hex);
}

export function parseStoredIndex(hex) {
  return {
    hex: String(hex || "").toUpperCase(),
    text: decodeBlock(hex),
    plan: decodeIndex(hex),
  };
}

function decodeIndex(hex) {
  const bytes = [];
  const raw = String(hex || "");
  for (let i = 0; i < raw.length; i += 2) bytes.push(parseInt(raw.substr(i, 2), 16));
  if (bytes.length < 11 || bytes[0] !== 0x43 || bytes[1] !== 0x50 || bytes[2] !== 1) return null;
  return FIELD_ORDER.map((_, index) => ({
    sector: bytes[3 + index * 2],
    block: bytes[4 + index * 2],
  }));
}

function sendReaderLine(payload) {
  return sendMessage(JSON.stringify({ type: "command", command: "write", payload }));
}

export function readMemberCard(cardpass) {
  return new Promise((resolve) => {
    if (!isWebSocketOpen() || cardReaderStatus.state !== "connected") {
      resolve({ status: "unread" });
      return;
    }
    const key = String(cardpass || "").trim();
    if (!key || key.length > 16 || /\s/.test(key)) {
      resolve({ status: "unread" });
      return;
    }
    writeBusyUntil = Date.now() + 8000;
    let step = "index";
    let plan = null;
    let cursor = 0;
    const raw = {};
    const text = {};
    let indexHex = "";
    const finish = (result) => {
      clearTimeout(timer);
      EventBus.$off("rfid-line", onLine);
      resolve(result);
    };
    const readNext = () => {
      const item = plan[cursor];
      sendReaderLine("READSEC " + item.sector + " " + item.block);
    };
    const timer = setTimeout(() => finish({ status: "unread", indexHex }), 8000);
    const onLine = (line) => {
      const block = parseReadSecLine(line);
      if (!block) return;
      if (step === "index" && block.sector === 1 && block.block === 0) {
        indexHex = block.hex;
        plan = decodeIndex(block.hex);
        if (!plan || plan.some((item) => item.sector < 2 || item.sector > 15 || item.block > 2)) {
          finish({ status: "blank", indexHex, indexText: decodeBlock(block.hex) });
          return;
        }
        step = "field";
        readNext();
        return;
      }
      if (step !== "field" || !plan) return;
      const current = plan[cursor];
      if (!current || block.sector !== current.sector || block.block !== current.block) return;
      const name = FIELD_ORDER[cursor];
      raw[name] = block.hex;
      text[name] = decodeBlock(block.hex);
      cursor += 1;
      if (cursor < plan.length) {
        readNext();
        return;
      }
      const balanceText = text.balance || "";
      const parts = balanceText.split("|");
      finish({
        status: "data",
        indexHex,
        indexText: decodeBlock(indexHex),
        index: plan.map((item, index) => ({
          field: FIELD_ORDER[index],
          sector: item.sector,
          block: item.block,
          text: text[FIELD_ORDER[index]] || "",
          hex: raw[FIELD_ORDER[index]] || "",
        })),
        phone: text.phone || "",
        cardNo: text.cardNo || "",
        balance: parts[0] || "",
        currency: parts[1] || "",
        points: text.points || "",
      });
    };
    EventBus.$on("rfid-line", onLine);
    ["MODE SECTOR", "CRYPTKEY " + key, "READSEC 1 0"].forEach((line, index) => {
      setTimeout(() => sendReaderLine(line), index * 280);
    });
  });
}

export function readMemberCardNo(cardpass) {
  return readMemberCard(cardpass).then((result) => {
    if (result.status === "data") return { status: "data", cardNo: result.cardNo };
    return { status: result.status };
  });
}

function openidOf() {
  const userinfo = JSON.parse(localStorage.getItem("user") || "null");
  return userinfo && userinfo.openid;
}

export function handleDetailSwipe(vm, hex) {
  const openid = openidOf();
  const id = vm.$route.query && vm.$route.query.id;
  if (!openid || !id) return Promise.resolve();
  vm.$message(vm.$t("menuitems.clouds.nfcreader.rfid.verifying"));
  return vm.$axios
    .post(vm.apiuri, { action: "getuserinfo", id }, { headers: { Openid: openid } })
    .then((response) => {
      if (!response.data || response.data.status !== 200) {
        vm.$message.error(vm.$t("global.delete.500"));
        return null;
      }
      return response.data.data;
    })
    .then((member) => {
      if (!member) return null;
      return vm.$axios
        .post(vm.apiuri, { action: "finduser", hex }, { headers: { Openid: openid } })
        .then((response) => ({ member, found: response.data || {} }));
    })
    .then((ctx) => {
      if (!ctx) return null;
      return readMemberCardNo(ctx.member.cardpass).then((sector) => ({ ...ctx, sector }));
    })
    .then((ctx) => {
      if (!ctx) return;
      const memberId = String(ctx.member.id);
      const found = ctx.found;
      let uidState = "free";
      if (found.status === 506) uidState = "banned";
      else if (found.status === 408) uidState = "admin";
      else if (found.status === 202) uidState = "whitelist";
      else if (found.status === 200) {
        uidState = String(found.data.id) === memberId ? "self" : "other";
      }
      const cardNo = String(ctx.member.cardno || "");
      let sectorState = "unread";
      if (ctx.sector.status === "blank") sectorState = "blank";
      else if (ctx.sector.status === "data") {
        sectorState = ctx.sector.cardNo === cardNo ? "match" : "mismatch";
      }
      if (uidState === "banned") {
        vm.$message.warning(vm.$t("menuitems.clouds.nfcreader.rfid.verifyBanned"));
        return;
      }
      if (uidState === "admin") {
        vm.$message.warning(vm.$t("menuitems.clouds.nfcreader.rfid.verifyAdmin"));
        return;
      }
      if (uidState === "whitelist") {
        vm.$message.warning(vm.$t("menuitems.clouds.nfcreader.rfid.verifyWhitelist"));
        return;
      }
      if (uidState === "other") {
        vm.$message.warning(vm.$t("menuitems.clouds.nfcreader.rfid.verifyOther"));
        return;
      }
      if (uidState !== "self" && sectorState === "mismatch") {
        vm.$message.warning(vm.$t("menuitems.clouds.nfcreader.rfid.verifyMismatch"));
        return;
      }
      if (sectorState === "unread") {
        const unbound = !String(ctx.member.hex || "").trim();
        if (unbound && (uidState === "free" || uidState === "self")) {
          sectorState = "blank";
        } else {
          vm.$message.warning(vm.$t("menuitems.clouds.nfcreader.rfid.verifyUnread"));
          return;
        }
      }
      if (uidState === "self" && sectorState === "match") {
        vm.$message.success(vm.$t("menuitems.clouds.nfcreader.rfid.verifyPass"));
        return;
      }
      const confirmKey = uidState === "self"
        ? "menuitems.clouds.nfcreader.rfid.verifyRewrite"
        : (sectorState === "match"
          ? "menuitems.clouds.nfcreader.rfid.verifyRebindMatch"
          : "menuitems.clouds.nfcreader.rfid.verifyRebind");
      vm.$confirm(vm.$t(confirmKey), vm.$t("global.delete.sureyouaction"), {
        confirmButtonText: vm.$t("global.button.ok"),
        cancelButtonText: vm.$t("global.button.cancel"),
        type: "warning",
      }).then(() => {
        if (uidState === "self") {
          notifyMemberCardWrite(vm, writeMemberCard(ctx.member));
          return;
        }
        vm.$axios
          .post(vm.apiuri, {
            action: "bindnewcard",
            hex,
            uid: vm.$route.query,
          }, { headers: { Openid: openid } })
          .then((response) => {
            if (response.data.status === 200) {
              writeMemberCardById(vm, id);
              if (typeof vm.getuserdetail === "function") vm.getuserdetail();
              vm.$message.success(vm.$t("global.ws.bindsuccess"));
            } else if (response.data.status === 409) {
              vm.$message.warning(vm.$t("menuitems.clouds.nfcreader.rfid.verifyOther"));
            } else if (response.data.status === 506) {
              vm.$message.warning(vm.$t("menuitems.clouds.nfcreader.rfid.verifyBanned"));
            } else if (response.data.status === 403) {
              vm.$message.warning(vm.$t("global.delete.auth"));
            } else {
              vm.$message.error(vm.$t("global.delete.500"));
            }
          });
      }).catch(() => {});
    })
    .catch(() => {
      vm.$message.error(vm.$t("global.errors.network"));
    });
}

export function writeMemberCardById(vm, id) {
  const userinfo = JSON.parse(localStorage.getItem("user") || "null");
  if (!id || !userinfo) return Promise.resolve("skip");
  return vm.$axios
    .post(vm.apiuri, { action: "getuserinfo", id }, { headers: { Openid: userinfo.openid } })
    .then((response) => {
      if (!response.data || response.data.status !== 200) return "skip";
      const result = writeMemberCard(response.data.data);
      notifyMemberCardWrite(vm, result);
      return result;
    })
    .catch(() => "skip");
}
